import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardHeader from "../DashboardHeader/DashboardHeader";
import StepHeader from "../StepHeader/StepHeader";
import ActionBar from "../ActionBar/ActionBar";
import { NotificationContainer, NotificationManager } from "react-notifications";
import { APP_STEPS } from "../config/steps";
import "./PersonalStatement.css";
import WhatsAppWidget from "../WhatsAppWidget/WhatsAppWidget";

const PersonalStatements = () => {
  // ---- state ----
  const [content, setContent] = useState("");
  const [howHeardAbout, setHowHeardAbout] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [personalStatement, setPersonalStatement] = useState(false);
  const [personalStatementId, setPersonalStatementId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const applicationId = parseInt(localStorage.getItem("id"), 10);
  const status = localStorage.getItem("application_status");
  const isDisabled = status !== "incomplete";
  const currentStep = 4; // index in APP_STEPS

  // ---- fetch existing statement ----
  useEffect(() => {
    const fetchData = async () => {
      try {
        const campaignRes = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/campaigns?sort=createdAt:desc&pagination[limit]=1`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const campaignResponse = await campaignRes.json();
        const latestCampaign = campaignResponse.data[0];
        if (!latestCampaign) return;

        const stmtRes = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/personal-statements?&filters[application][id][$eq]=${applicationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const personal_statements = await stmtRes.json();
        const statements = personal_statements.data;

        if (statements.length > 0) {
          setContent(statements[0].personal_statement || "");
          setHowHeardAbout(statements[0].detail || "");
          setPersonalStatement(true);
          setPersonalStatementId(statements[0].documentId);
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching personal statement:", err);
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- word count helpers ----
  const countWords = (text) => text.trim().split(/\s+/).length;

  // ---- input change with word limit ----
  const handleInputChange = (e) => {
    const wordCount = countWords(e.target.value);
    if (wordCount <= 800) {
      setContent(e.target.value);
      setError(null);
    } else {
      setError("Word count exceeds 800");
    }
  };

  // ---- paste handler ----
  const handlePaste = (e) => {
    const pastedData = e.clipboardData.getData("text");
    const wordCount = countWords(content + pastedData);
    if (wordCount > 800) {
      setError("Word count exceeds 800");
      e.preventDefault();
    }
  };

  // ---- submit (existing API logic kept) ----
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (isDisabled) return;
    if (status === "new") { navigate("/application/review"); return; }
    setErrorMessage("");

    if (!content) {
      setErrorMessage("Please fill out Personal Statement.");
      return;
    }

    if (!howHeardAbout) {
      setErrorMessage("Please tell us how you heard about this scholarship.");
      return;
    }

    setSaving(true);
    const postData = { personal_statement: content, detail: howHeardAbout };

    try {
      if (personalStatement) {
        // Update existing
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/personal-statements/${personalStatementId}`,
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ data: postData }),
          }
        );
        if (!response.ok) throw new Error("Failed to submit form");
        setSaving(false);
        navigate("/application/review");
      } else {
        // Create new
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/personal-statements`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ data: { ...postData, applicationId } }),
          }
        );
        if (!response.ok) throw new Error("Failed to submit form");

        const data = await response.json();
        localStorage.setItem("personal_statement", data.id);

        setSaving(false);
        navigate("/application/review");
      }
    } catch (err) {
      console.error("Error submitting personal statement:", err);
      setSaving(false);
      NotificationManager.error("Failed to submit. Please try again.");
    }
  };

  const wordCount = countWords(content);
  const wordLimit = 800;

  return (
    <div>
      {/* Top bar */}
      <DashboardHeader />

      {/* Step progress */}
      <div style={{ background: "rgba(255,255,255,.97)", borderBottom: "1px solid #dbe3e1" }}>
        <div className="ps" style={{ paddingBottom: 0 }}>
          <StepHeader steps={APP_STEPS} current={currentStep} />
        </div>
      </div>

      {/* Content */}
      <div className="ps">
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
            <div className="spinner-bubble spinner-bubble-primary"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* page heading */}
            <h2 className="ps__title">Personal statement</h2>
            <p className="ps__sub">Tell us about yourself in your own words.</p>
            <p className="ps__req-note">Fields marked <em style={{ color: "#a9443b" }}>*</em> are required.</p>

            <div className="ps__card">
              <div className={`ps__field${error ? " invalid" : ""}`}>
                <label className="ps__label">Why do you need this scholarship? <em style={{ color: "#a9443b" }}>*</em></label>
                <textarea
                  className="ps__textarea"
                  placeholder="Write here…"
                  value={content || ""}
                  onPaste={handlePaste}
                  onChange={handleInputChange}
                  disabled={isDisabled}
                  rows={12}
                />

                {/* Word count */}
                <div className="ps__wc">
                  <span>{wordCount < 150 ? "Minimum 150 words" : ""}</span>
                  <span>
                    <b className={wordCount >= 150 && wordCount <= wordLimit ? "ok" : ""}>{wordCount}</b>
                    {" "}/ {wordLimit} words
                  </span>
                </div>

                {error && <p className="ps__err">{error}</p>}
                {errorMessage && <p className="ps__err" style={{ display: "block" }}>{errorMessage}</p>}

                {/* Writing prompts */}
                <div className="ps__prompts">
                  <b>You can write about:</b>
                  <ul>
                    <li>Your study or career goals</li>
                    <li>Challenges you have faced</li>
                    <li>How this scholarship will help you and your family</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* How did you hear about this scholarship */}
            <div className="ps__card">
              <div className="ps__field">
                <label className="ps__label">How did you hear about this scholarship? <em style={{ color: "#a9443b" }}>*</em></label>
                <textarea
                  className="ps__textarea"
                  placeholder="e.g. Social media, friend, university, website, newspaper…"
                  value={howHeardAbout || ""}
                  onChange={(e) => setHowHeardAbout(e.target.value)}
                  disabled={isDisabled}
                  rows={4}
                  style={{ minHeight: "120px" }}
                />
                <p className="ps__hint">Tell us the source — this helps us reach more students like you.</p>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Bottom action bar */}
      <ActionBar
        onBack={() => navigate("/application/upload-documents")}
        onNext={handleSubmit}
        backLabel="← Back"
        nextLabel="Continue →"
        nextDisabled={isDisabled}
        nextLoading={saving}
      />

      <NotificationContainer />
      <WhatsAppWidget />
    </div>
  );
};

export default PersonalStatements;
